import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";

const RealtimeContext = createContext({ connected: false });
export const useRealtime = () => useContext(RealtimeContext);

const wsBase = () => (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

// Single authenticated WebSocket for the whole app. On any server-side change the
// backend pushes {type:'invalidate', topics:[...]} which we re-broadcast as a
// `daa-realtime` window event. useAutoRefresh and page listeners react to it for
// instant updates; the existing 20s polling stays as a fallback when the socket is down.
export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!user) return () => {};
    // Per-effect-run state so a StrictMode double-mount (or a reconnect racing with
    // teardown) can never leave a duplicate persistent connection behind.
    let cancelled = false;
    let ws = null;
    let pingId = null;
    let retry = 0;

    const scheduleRetry = () => {
      if (cancelled) return;
      retry = Math.min(retry + 1, 6);
      const delay = Math.min(1000 * 2 ** (retry - 1), 30000);
      setTimeout(() => { if (!cancelled) connect(); }, delay);
    };

    function connect() {
      if (cancelled) return;
      const token = localStorage.getItem("daa_token");
      if (!token) return;
      try { ws = new WebSocket(`${wsBase()}/api/ws?token=${encodeURIComponent(token)}`); }
      catch { scheduleRetry(); return; }
      ws.onopen = () => {
        if (cancelled) { try { ws.close(); } catch { /* noop */ } return; }
        retry = 0;
        setConnected(true);
        pingId = setInterval(() => { try { if (ws.readyState === 1) ws.send("ping"); } catch { /* noop */ } }, 25000);
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg && msg.type === "invalidate") {
            window.dispatchEvent(new CustomEvent("daa-realtime", { detail: { topics: msg.topics || [] } }));
          }
        } catch { /* ignore malformed */ }
      };
      ws.onclose = (ev) => {
        setConnected(false);
        if (pingId) clearInterval(pingId);
        if (ev && ev.code === 1008) { cancelled = true; return; }  // auth rejected (bad/expired token, deactivated user) — stop retrying
        scheduleRetry();
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    }

    connect();
    const onFocus = () => { if (cancelled) return; if (!ws || ws.readyState > 1) connect(); };
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      if (pingId) clearInterval(pingId);
      try { ws && ws.close(); } catch { /* noop */ }
    };
  }, [user]);

  return <RealtimeContext.Provider value={{ connected }}>{children}</RealtimeContext.Provider>;
}
